import { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import axios from 'axios';
import VotingABI from '../abi/Voting.json';
import CandidateImage from './CandidateImage';

const CONTRACT_ADDRESS = process.env.REACT_APP_CONTRACT_ADDRESS;
const SEPOLIA_RPC_URL  = process.env.REACT_APP_SEPOLIA_RPC_URL;
const BACKEND_URL      = process.env.REACT_APP_BACKEND_URL;
const ETHERSCAN_BASE   = 'https://sepolia.etherscan.io/address/';

export default function P07FinalResult({ isAdmin }) {
  const [candidates, setCandidates] = useState([]);
  const [totalVotes, setTotalVotes] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => { loadResults(); }, []);

  async function loadResults() {
    try {
      const provider = new ethers.JsonRpcProvider(SEPOLIA_RPC_URL);
      const contract = new ethers.Contract(CONTRACT_ADDRESS, VotingABI.abi, provider);
      const [onchain, total] = await Promise.all([
        contract.getCandidates(),
        contract.getTotalVotes(),
      ]);
      let offchain = [];
      try { offchain = await axios.get(BACKEND_URL + '/candidates').then(r => r.data); } catch {}
      const merged = onchain.map(c => {
        const off = offchain.find(o => o.id === Number(c.id)) || {};
        return { id: Number(c.id), name: c.name, voteCount: Number(c.voteCount),
          imageUrl: off.imageUrl || null, description: off.description || '' };
      });
      const sorted = [...merged].sort((a, b) => b.voteCount - a.voteCount);
      setCandidates(sorted);
      setTotalVotes(Number(total));
      setLoaded(true);
    } catch (e) { console.error('결과 로딩 오류:', e); }
  }

  if (!loaded) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '50vh', color: 'var(--text-sub)' }}>
      결과를 불러오는 중...
    </div>
  );

  const maxVotes = candidates.length > 0 ? Math.max(...candidates.map(c => c.voteCount)) : 0;
  const winners = candidates.filter(c => c.voteCount === maxVotes && maxVotes > 0);

  return (
    <div className="ev-page">
      <div className="ev-page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <span className="ev-status-badge ev-status-ended">● 투표 종료</span>
        </div>
        <h1 className="ev-page-title">최종 투표 결과</h1>
        <div className="ev-total-count">
          총 투표 수 <strong>{totalVotes}표</strong>
        </div>
      </div>

      {winners.length > 0 && (
        <div className="ev-final-winner-box">
          <div className="ev-final-winner-icon">🏆</div>
          <div>
            <div className="ev-final-winner-label">
              {winners.length === 1 ? '당선' : '공동 당선'}
            </div>
            <div className="ev-final-winner-name">
              {winners.map(w => w.name).join(' · ')}
            </div>
            {winners.length === 1
              ? <div style={{ fontSize: '0.85rem', color: 'var(--text-sub)', marginTop: 4 }}>
                  {winners[0].voteCount}표 ({totalVotes > 0 ? ((winners[0].voteCount / totalVotes) * 100).toFixed(1) : 0}%)
                </div>
              : <div style={{ fontSize: '0.85rem', color: 'var(--text-sub)', marginTop: 4 }}>
                  각 {maxVotes}표로 공동 당선
                </div>
            }
          </div>
        </div>
      )}

      <div className="ev-result-list">
        {candidates.map((c, idx) => {
          const barW = maxVotes > 0 ? ((c.voteCount / maxVotes) * 100).toFixed(0) : 0;
          const pct = totalVotes > 0 ? ((c.voteCount / totalVotes) * 100).toFixed(1) : '0.0';
          const isWinner = c.voteCount === maxVotes && maxVotes > 0;
          return (
            <div key={c.id} className={`ev-result-item ${isWinner ? 'ev-result-item--leader' : ''}`}>
              <div className="ev-result-item__rank">{idx + 1}</div>
              <CandidateImage
                src={c.imageUrl ? BACKEND_URL + c.imageUrl : null}
                alt={c.name}
                circle
                width={60}
                height={60}
              />
              <div className="ev-result-item__info">
                <div className="ev-result-item__name">
                  기호 {c.id + 1}번 &nbsp;{c.name}
                  {isWinner && <span className="ev-winner-badge">당선</span>}
                </div>
                {c.description && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-sub)', marginBottom: 4 }}>
                    {c.description}
                  </div>
                )}
                <div className="ev-progress-wrap" style={{ marginTop: 8 }}>
                  <div className={`ev-progress-bar ${isWinner ? 'ev-progress-bar--leader' : ''}`}
                    style={{ width: `${barW}%` }} />
                </div>
              </div>
              <div>
                <div className="ev-result-item__pct">{pct}%</div>
                <div className="ev-result-item__count">{c.voteCount}표</div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
        <a className="ev-etherscan-link"
          href={ETHERSCAN_BASE + CONTRACT_ADDRESS}
          target="_blank" rel="noopener noreferrer">
          🔗 블록체인에서 결과 확인하기 (Etherscan)
        </a>
      </div>

      <div className="ev-ended-notice">
        <strong>이 선거는 영구적으로 종료되었습니다.</strong>
        결과는 Sepolia 블록체인에 영구적으로 기록되어 변경할 수 없습니다.
        {isAdmin && (
          <>
            {' '}새로운 선거를 시작하려면 스마트 컨트랙트를 새로 배포한 후
            프론트엔드의 컨트랙트 주소를 업데이트해야 합니다.
          </>
        )}
      </div>
    </div>
  );
}
